import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
  JobApplicationModal, 
  SimilarJobsSection, 
  CompanyCultureSection, 
  JobShareModal 
} from './components';
import { LoadingSpinner } from '../../components/layout';
import { MarkdownRenderer } from '../../components/ui/MarkdownRenderer';
import { AIEditableWrapper, AIImageGenerator } from '../../components/admin/inline-editor';
import { useAuth } from '../../hooks/useAuth';
import { useViewport } from '../../hooks/useViewport';
import { ResumeSelectionModal } from '../../components/applications/ResumeSelectionModal';
import { useJob, useTrackJobView } from '../../hooks/unified/useJobsQueries';
import { useCreateApplication, useHasAppliedToJob } from '../../hooks/unified/useApplicationsQueries';
import { useIsJobSaved, useSaveJob, useUnsaveJob } from '../../hooks/unified/useSavedJobsQueries';
import { useToast } from '../../contexts/ToastContext';
import { useEventTracking } from '../../hooks/useAnalytics';
import { unifiedApiService } from '../../services/unified';
import { env } from '../../config/env';
import type { Job } from '../../types/job';
import type { Company } from '../../services/unified/types';

// Helper function to get full image URL
const getImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  return `${env.API_BASE_URL}${imageUrl}`;
};

// Extended Job type that merges both legacy and API types
type ExtendedJob = Job & {
  company_logo_url?: string;
  job_image_url?: string;
  salary_min?: number;
  salary_max?: number;
  job_type?: string;
  experience_level?: string;
};

// No mock data - use real API data only

export function JobDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isAdmin } = useAuth();
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { trackJobView } = useEventTracking();
  const trackJobViewMutation = useTrackJobView();
  
  const [job, setJob] = useState<ExtendedJob | null>(null);
  const [companyDetails, setCompanyDetails] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showResumeSelectionModal, setShowResumeSelectionModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if apply modal should be shown from URL params
  const shouldShowApplyModal = searchParams.get('apply') === 'true';
  const shouldSaveJob = searchParams.get('save') === 'true';
  
  // Saved jobs hooks
  const { data: isJobSaved, isLoading: isCheckingSaved } = useIsJobSaved(id || '', isAuthenticated);
  const saveJobMutation = useSaveJob();
  const unsaveJobMutation = useUnsaveJob();
  const isBookmarked = isJobSaved ?? false;
  const isBookmarking = saveJobMutation.isPending || unsaveJobMutation.isPending || isCheckingSaved;
  
  // Use unified API to fetch job data
  const { data: apiJob } = useJob(id || '');
  const createApplicationMutation = useCreateApplication();
  
  // Check if user has already applied to this job
  const { hasApplied: userHasApplied } = useHasAppliedToJob(id || '', isAuthenticated);

  useEffect(() => {
    const loadJob = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Use only real API data - no mock data
        
        if (apiJob) {
          // Convert API job to extended job format
          const jobData: ExtendedJob = {
            ...apiJob,
            // Map API fields to legacy fields
            type: apiJob.job_type as any,
            experience: apiJob.experience_level as any,
            salary: apiJob.salary_min && apiJob.salary_max 
              ? `INR ${apiJob.salary_min.toLocaleString()} - ${apiJob.salary_max.toLocaleString()}`
              : apiJob.salary_min 
              ? `From INR ${apiJob.salary_min.toLocaleString()}`
              : 'Competitive Salary',
            companyLogo: apiJob.company_logo_url,
            postedDate: apiJob.created_at || apiJob.published_at || new Date().toISOString(),
            // Keep enhanced API fields
            company_logo_url: apiJob.company_logo_url,
            job_image_url: apiJob.job_image_url,
            salary_min: apiJob.salary_min,
            salary_max: apiJob.salary_max,
            job_type: apiJob.job_type,
            experience_level: apiJob.experience_level,
          };
          
          setJob(jobData);
          
          // Fetch company details if company_id is available
          if ((apiJob as any).company_id) {
            try {
              const company = await unifiedApiService.companies.getCompanyPublic((apiJob as any).company_id);
              setCompanyDetails(company);
            } catch (companyErr) {
              console.error('Failed to fetch company details:', companyErr);
              // Don't fail the whole page if company fetch fails
            }
          }
          
          // Track job view event (client-side analytics)
          trackJobView(jobData.id.toString(), jobData.title);
          
          // Track job view in backend API
          trackJobViewMutation.mutate({ 
            jobId: jobData.id.toString(), 
            viewData: { referrer: document.referrer } 
          });
          
          // Show resume selection modal if requested
          if (shouldShowApplyModal && isAuthenticated && !jobData.hasApplied) {
            setShowResumeSelectionModal(true);
          }
        } else {
          setError('Job not found');
        }
      } catch (err) {
        console.error('Error loading job:', err);
        setError('Failed to load job details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id, apiJob?.id]); // Only depend on ID to avoid infinite loops

  // Handle save action after login redirect
  useEffect(() => {
    if (shouldSaveJob && isAuthenticated && job && !isBookmarked && !isCheckingSaved) {
      saveJobMutation.mutate(job.id.toString(), {
        onSuccess: () => {
          showSuccess('Job Saved', 'Job added to your saved list.');
          // Remove the save param from URL
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('save');
          navigate(`${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
        },
      });
    }
  }, [shouldSaveJob, isAuthenticated, job, isBookmarked, isCheckingSaved]);

  const handleBookmark = async () => {
    if (!job) return;
    
    // If not authenticated, redirect to login with return URL including save action
    if (!isAuthenticated) {
      const returnUrl = `${window.location.pathname}?save=true`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    if (isBookmarking) return;
    
    try {
      if (isBookmarked) {
        await unsaveJobMutation.mutateAsync(job.id.toString());
        showSuccess('Job Removed', 'Job removed from your saved list.');
      } else {
        await saveJobMutation.mutateAsync(job.id.toString());
        showSuccess('Job Saved', 'Job added to your saved list.');
      }
    } catch {
      showError('Error', 'Failed to update saved status. Please try again.');
    }
  };

  const handleApply = () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    
    // Check if already applied
    if (userHasApplied) {
      navigate('/dashboard/applications');
      return;
    }
    
    setShowResumeSelectionModal(true);
  };

  // Native share handler for mobile - uses Web Share API when available
  const handleShare = async () => {
    if (!job) return;
    
    const jobUrl = `${window.location.origin}/jobs/${job.id}`;
    const shareData = {
      title: `${job.title} at ${job.company}`,
      text: `Check out this job opportunity: ${job.title} at ${job.company}`,
      url: jobUrl,
    };

    // Use native share on mobile if available
    if (navigator.share && isMobile) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // User cancelled or share failed - fall back to modal
        if ((err as Error).name === 'AbortError') {
          return; // User cancelled, don't show modal
        }
      }
    }
    
    // Fallback to share modal
    setShowShareModal(true);
  };

  const handleResumeSelected = async (resumeId: string) => {
    if (!job?.id) return;
    
    try {
      await createApplicationMutation.mutateAsync({
        job_id: job.id,
        resume_id: resumeId,
      });
      
      showSuccess('Application Submitted', 'Your application has been submitted successfully!');
      
      // Update job state to reflect application
      setJob(prev => prev ? { ...prev, hasApplied: true } : null);
      
      // Close modal
      setShowResumeSelectionModal(false);
      
      // Redirect to applications page after a short delay
      setTimeout(() => {
        navigate('/dashboard/applications');
      }, 1500);
    } catch (err: any) {
      showError('Application Failed', err.message || 'Failed to submit application. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'remote':
        return 'bg-green-100 text-green-800';
      case 'contract':
        return 'bg-yellow-100 text-yellow-800';
      case 'part-time':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExperienceColor = (experience?: string) => {
    switch (experience) {
      case 'entry':
        return 'bg-green-100 text-green-800';
      case 'mid':
        return 'bg-blue-100 text-blue-800';
      case 'senior':
        return 'bg-purple-100 text-purple-800';
      case 'executive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The job you are looking for does not exist.'}</p>
          <Link
            to="/jobs"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Mobile optimized with vertical stacking */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6">
            {/* Job Header - Stacked vertically on mobile */}
            <div className="flex-1 min-w-0">
              {/* Mobile: Stack logo, title, metadata vertically */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                {/* Company Logo - Centered on mobile */}
                {((job as any).company_logo_url || job.companyLogo) && getImageUrl((job as any).company_logo_url || job.companyLogo) && (
                  <img
                    src={getImageUrl((job as any).company_logo_url || job.companyLogo)!}
                    alt={`${job.company} logo`}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover bg-gray-100 flex-shrink-0 mx-auto sm:mx-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  {/* Title - Smaller on mobile */}
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
                  {/* Company & Location - Stacked on mobile */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-gray-600 mb-3">
                    <div className="flex items-center justify-center sm:justify-start">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium text-sm sm:text-base">{job.company}</span>
                    </div>
                    <div className="flex items-center justify-center sm:justify-start">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm sm:text-base">{job.location}</span>
                    </div>
                  </div>
                  
                  {/* Tags - Centered on mobile */}
                  <div className="flex flex-wrap justify-center sm:justify-start gap-2 mb-3 sm:mb-4">
                    {((job as any).job_type || job.type) && (
                      <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getTypeColor((job as any).job_type || job.type)}`}>
                        {((job as any).job_type || job.type).replace('-', ' ').toUpperCase()}
                      </span>
                    )}
                    {((job as any).experience_level || job.experience) && (
                      <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getExperienceColor((job as any).experience_level || job.experience)}`}>
                        {((job as any).experience_level || job.experience).toUpperCase()} LEVEL
                      </span>
                    )}
                    {job.featured && (
                      <span className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r from-yellow-400 to-orange-400 text-white">
                        FEATURED
                      </span>
                    )}
                  </div>

                  {/* Salary - Smaller on mobile */}
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                    {(() => {
                      const apiJobData = job as any;
                      if (apiJobData.salary_min && apiJobData.salary_max) {
                        return `INR ${apiJobData.salary_min.toLocaleString()} - ${apiJobData.salary_max.toLocaleString()}`;
                      } else if (apiJobData.salary_min) {
                        return `From INR ${apiJobData.salary_min.toLocaleString()}`;
                      } else if (job.salary) {
                        return job.salary;
                      }
                      return 'Competitive Salary';
                    })()}
                  </div>

                  {/* Job Stats - Wrap on mobile */}
                  <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Posted {formatDate(job.postedDate)}</span>
                    </div>
                    {job.applicationCount !== undefined && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{job.applicationCount} applicants</span>
                      </div>
                    )}
                    {job.applicationDeadline && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Apply by {formatDate(job.applicationDeadline)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Hidden on mobile (shown in sticky footer instead) */}
            <div className="hidden sm:flex flex-col sm:flex-row lg:flex-col gap-3 lg:w-64 flex-shrink-0">
              {(job.hasApplied || userHasApplied) ? (
                <Link
                  to="/dashboard/applications"
                  className="flex items-center justify-center px-6 py-3 bg-green-100 text-green-800 rounded-lg font-semibold hover:bg-green-200 transition-colors min-h-[44px]"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Applied - View Application
                </Link>
              ) : (
                <button
                  onClick={handleApply}
                  className="flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl min-h-[44px]"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Apply Now
                </button>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleBookmark}
                  disabled={isBookmarking}
                  className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all duration-200 min-h-[44px] ${
                    isBookmarked
                      ? 'bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100'
                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50'
                  }`}
                  title={isAuthenticated ? (isBookmarked ? 'Remove from saved' : 'Save job') : 'Login to save job'}
                >
                  <svg className={`w-5 h-5 mr-2 ${isBookmarked ? 'fill-current' : ''}`} fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {isBookmarking ? 'Saving...' : isBookmarked ? 'Saved' : 'Save'}
                </button>
                
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200 min-h-[44px]"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Job Image Banner - Responsive aspect ratio (taller on mobile, wider on desktop) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {(job as any).job_image_url && getImageUrl((job as any).job_image_url) ? (
          <div className="relative rounded-xl overflow-hidden shadow-lg group aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]">
            <img
              src={getImageUrl((job as any).job_image_url)!}
              alt={`${job.title} banner`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            {/* AI Image Generator Button - Admin Only (floating style, 30% opacity) */}
            {isAdmin && (
              <div className="absolute top-4 right-4">
                <AIImageGenerator
                  jobId={job.id.toString()}
                  currentImageUrl={getImageUrl((job as any).job_image_url)}
                  onImageGenerated={(imageUrl) => {
                    setJob(prev => prev ? { ...prev, job_image_url: imageUrl } : null);
                  }}
                  floatingButton
                />
              </div>
            )}
          </div>
        ) : isAdmin ? (
          <div className="bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 p-8 text-center aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] flex flex-col items-center justify-center">
            <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 mb-4">No job image yet. Generate one with AI!</p>
            <AIImageGenerator
              jobId={job.id.toString()}
              onImageGenerated={(imageUrl) => {
                setJob(prev => prev ? { ...prev, job_image_url: imageUrl } : null);
              }}
            />
          </div>
        ) : null}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Job Description */}
            <AIEditableWrapper
              jobId={job.id.toString()}
              componentType="description"
              onContentUpdated={(newContent) => {
                setJob(prev => prev ? { ...prev, description: newContent as string } : null);
              }}
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Description</h2>
                <MarkdownRenderer 
                  content={job.description || ''} 
                  className="text-gray-700"
                />
              </div>
            </AIEditableWrapper>

            {/* Requirements - Single column on mobile, multi-column on larger screens */}
            {(() => {
              const requirements = (job as any).requirements || job.requirements;
              return requirements && requirements.length > 0 && (
                <AIEditableWrapper
                  jobId={job.id.toString()}
                  componentType="requirements"
                  onContentUpdated={(newContent) => {
                    setJob(prev => prev ? { ...prev, requirements: newContent as string[] } : null);
                  }}
                >
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Requirements</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {requirements.map((requirement: string, index: number) => (
                        <div
                          key={index}
                          className="flex items-center px-3 py-2.5 sm:py-2 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium min-h-[44px] sm:min-h-0"
                        >
                          <svg className="w-4 h-4 mr-2 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="truncate">{requirement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AIEditableWrapper>
              );
            })()}

            {/* Company Culture */}
            {job.companyCulture && (
              <CompanyCultureSection culture={job.companyCulture} />
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Benefits - Single column with adequate spacing on mobile */}
            {(() => {
              const benefits = (job as any).benefits || job.benefits;
              return benefits && benefits.length > 0 && (
                <AIEditableWrapper
                  jobId={job.id.toString()}
                  componentType="benefits"
                  onContentUpdated={(newContent) => {
                    setJob(prev => prev ? { ...prev, benefits: newContent as string[] } : null);
                  }}
                >
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Benefits & Perks</h3>
                    <div className="space-y-2 sm:space-y-3">
                      {benefits.map((benefit: string, index: number) => (
                        <div key={index} className="flex items-center text-gray-700 py-1 sm:py-0 min-h-[44px] sm:min-h-0">
                          <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm sm:text-base">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AIEditableWrapper>
              );
            })()}

            {/* Company Info */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">About {companyDetails?.name || job.company}</h3>
              
              {/* Company Description - prefer fetched company details */}
              {(companyDetails?.description || job.companyDescription) && (
                <p className="text-gray-700 leading-relaxed mb-4">
                  {companyDetails?.description || job.companyDescription}
                </p>
              )}
              
              <div className="space-y-3 text-sm">
                {/* Industry */}
                {companyDetails?.industry && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>{companyDetails.industry}</span>
                  </div>
                )}
                
                {/* Company Size */}
                {companyDetails?.company_size && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>{companyDetails.company_size} employees</span>
                  </div>
                )}
                
                {/* Headquarters */}
                {companyDetails?.headquarters && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>
                      {(() => {
                        const hq = companyDetails.headquarters;
                        // Handle JSON string
                        if (typeof hq === 'string') {
                          try {
                            const parsed = JSON.parse(hq);
                            return `${parsed.city}${parsed.state ? `, ${parsed.state}` : ''}${parsed.country ? `, ${parsed.country}` : ''}`;
                          } catch {
                            return hq;
                          }
                        }
                        // Handle object
                        return `${hq.city}${hq.state ? `, ${hq.state}` : ''}${hq.country ? `, ${hq.country}` : ''}`;
                      })()}
                    </span>
                  </div>
                )}
                
                {/* Founded Year */}
                {companyDetails?.founded_year && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Founded {companyDetails.founded_year}</span>
                  </div>
                )}
                
                {/* Website */}
                {companyDetails?.website && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <a 
                      href={companyDetails.website.startsWith('http') ? companyDetails.website : `https://${companyDetails.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {companyDetails.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                
                {/* Job Location (fallback if no company details) */}
                {!companyDetails?.headquarters && job.location && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{job.location}</span>
                  </div>
                )}
                
                {job.companyCulture?.teamSize && !companyDetails?.company_size && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>{job.companyCulture.teamSize}</span>
                  </div>
                )}
              </div>
              

            </div>

            {/* Quick Apply */}
            {!(job.hasApplied || userHasApplied) && (
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Quick Apply</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Don't miss out on this opportunity. Apply now and get noticed by the hiring team.
                </p>
                <button
                  onClick={handleApply}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
                >
                  Apply Now
                </button>
              </div>
            )}
            
            {/* Already Applied */}
            {(job.hasApplied || userHasApplied) && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Application Submitted</h3>
                <p className="text-gray-600 text-sm mb-4">
                  You've already applied for this position. Track your application status in your dashboard.
                </p>
                <Link
                  to="/dashboard/applications"
                  className="block w-full bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors duration-200 text-center"
                >
                  View My Applications
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Similar Jobs */}
        <SimilarJobsSection jobId={job.id} />
      </div>

      {/* Modals */}
      {showResumeSelectionModal && job && (
        <ResumeSelectionModal
          isOpen={showResumeSelectionModal}
          onClose={() => setShowResumeSelectionModal(false)}
          onResumeSelected={handleResumeSelected}
          jobId={job.id}
          jobTitle={job.title}
        />
      )}

      {showApplicationModal && (
        <JobApplicationModal
          job={job}
          onClose={() => setShowApplicationModal(false)}
          onSuccess={() => {
            setShowApplicationModal(false);
            // Update job state to reflect application
            setJob(prev => prev ? { ...prev, hasApplied: true } : null);
          }}
        />
      )}

      {showShareModal && (
        <JobShareModal
          job={job}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Mobile Sticky Apply Button - Fixed at bottom on mobile */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 safe-area-inset-bottom z-40 shadow-lg">
          <div className="flex items-center gap-2 max-w-7xl mx-auto">
            {/* Save Button */}
            <button
              onClick={handleBookmark}
              disabled={isBookmarking}
              className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200 min-w-[44px] min-h-[44px] ${
                isBookmarked
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-gray-100 text-gray-700 border border-gray-200'
              }`}
              aria-label={isBookmarked ? 'Remove from saved' : 'Save job'}
            >
              <svg className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
            
            {/* Share Button */}
            <button
              onClick={handleShare}
              className="flex items-center justify-center p-3 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg transition-colors duration-200 min-w-[44px] min-h-[44px]"
              aria-label="Share job"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>
            
            {/* Apply Button - Takes remaining space */}
            {(job.hasApplied || userHasApplied) ? (
              <Link
                to="/dashboard/applications"
                className="flex-1 flex items-center justify-center px-4 py-3 bg-green-100 text-green-800 rounded-lg font-semibold min-h-[44px]"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                View Application
              </Link>
            ) : (
              <button
                onClick={handleApply}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg font-semibold shadow-lg min-h-[44px]"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Apply Now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Spacer for mobile sticky button */}
      {isMobile && <div className="h-20" />}
    </div>
  );
}